import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down';

interface ScrollState {
  direction: ScrollDirection;
  scrollY: number;
}

/**
 * Tracks the window's scroll direction and position. Used to drive the
 * progressive (hide-on-scroll-down / show-on-scroll-up) navigation.
 *
 * The page scrolls on the window — the page's `<main>` has `overflow-auto` but
 * its height is unconstrained, so it grows to fit its content and the window is
 * the actual scroll container.
 *
 * @param threshold Minimum delta (px) before a direction change is registered,
 *                  to avoid flicker on tiny scroll jitters.
 */
export function useScrollDirection(threshold = 8): ScrollState {
  const [state, setState] = useState<ScrollState>({
    direction: 'up',
    scrollY: 0,
  });
  const lastY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;

      if (Math.abs(delta) < threshold) return;

      setState({ direction: delta > 0 ? 'down' : 'up', scrollY: y });
      lastY.current = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return state;
}
